/* eslint-disable react/prop-types */

import React, { useMemo, useState } from 'react';
import HashtagTable from './hashtag_table';
import setFollowStatus from './follow_actions';

function SearchHashtagsView({
                              searchResults,
                              isSearching,
                              searchTerm,
                              handleFollow,
                              handleMute,
                              handleBlock,
                              hashtagUsageCeiling,
                              profileId,
                            }) {
  // State to manage local UI updates before API calls complete
  const [localResults, setLocalResults] = useState(searchResults);

  // Update local state when props change
  React.useEffect(() => {
    setLocalResults(searchResults);
  }, [searchResults]);

  // Filter and sort the search results
  const filteredResults = useMemo(() => {
    // Check if a hashtag is followable (not high volume)
    const highFrequency = (hashtag) => !(hashtagUsageCeiling && hashtag.hashtag_usage_count >= hashtagUsageCeiling);

    // Get currently followed hashtags
    const trulyFollowed = localResults.followed.filter((hashtag) => hashtag.follow_status === 'follow');

    // Get muted/blocked hashtags and combine with not followed
    const muted = localResults.followed.filter((hashtag) => hashtag.follow_status !== 'follow');

    // Combine all muted/not followed hashtags and treat null status as muted
    const allMuted = [
      ...muted,
      ...localResults.notFollowed.map((hashtag) => ({
        ...hashtag,
        follow_status: hashtag.follow_status === null ? 'mute' : hashtag.follow_status,
      })),
    ];

    // Sort by followability (followable first) then by usage count
    const sortedMuted = allMuted.sort((a, b) => {
      // First sort by followability
      const aFollowable = highFrequency(a);
      const bFollowable = highFrequency(b);

      if (aFollowable !== bFollowable) {
        return aFollowable ? -1 : 1; // Followable hashtags first
      }

      // Then sort by usage count (descending)
      return b.hashtag_usage_count - a.hashtag_usage_count;
    });

    return {
      followed: trulyFollowed,
      notFollowed: sortedMuted,
    };
  }, [localResults, hashtagUsageCeiling]);

  // Helper to update local state for UI feedback
  const updateLocalState = (id, newStatus) => {
    setLocalResults((prev) => {
      // Create deep copies of the arrays to avoid mutating state
      const newFollowed = [...prev.followed].map((hashtag) => (
        hashtag.hashtag_id === id ? { ...hashtag, follow_status: newStatus } : hashtag
      ));

      const newNotFollowed = [...prev.notFollowed].map((hashtag) => (
        hashtag.hashtag_id === id ? { ...hashtag, follow_status: newStatus } : hashtag
      ));

      return {
        followed: newFollowed,
        notFollowed: newNotFollowed,
      };
    });
  };

  // Handlers for different actions
  const onFollow = (id, isHighVolume) => {
    updateLocalState(id, 'follow');
    handleFollow('MatchFeed::Hashtag', id, isHighVolume);
  };

  const onMute = (id) => {
    updateLocalState(id, 'mute');
    handleMute('MatchFeed::Hashtag', id);
  };

  const onRemoveForceFollow = (id) => {
    // Only update the force_followed_at property without changing follow_status
    setLocalResults((prev) => {
      // Create deep copies of arrays
      const newFollowed = [...prev.followed].map((hashtag) => (
        hashtag.hashtag_id === id ? { ...hashtag, force_followed_at: null } : hashtag
      ));

      const newNotFollowed = [...prev.notFollowed].map((hashtag) => (
        hashtag.hashtag_id === id ? { ...hashtag, force_followed_at: null } : hashtag
      ));

      return {
        followed: newFollowed,
        notFollowed: newNotFollowed,
      };
    });

    // Call the API to remove force-follow
    setFollowStatus({
      followableType: 'MatchFeed::Hashtag',
      followableId: id,
      forceFollow: false,
      setIsActionLoading: () => {
      },
      onlyUpdateForceFollow: true,
      profileId,
    });
  };

  const onBlock = (id) => {
    updateLocalState(id, 'block');
    handleBlock('MatchFeed::Hashtag', id);
  };

  return (
    <div className="mb-4">
      {!isSearching && !filteredResults.followed.length && !filteredResults.notFollowed.length && (
        <div>No hashtags found matching &ldquo;{searchTerm}&rdquo;</div>
      )}

      {!isSearching && (filteredResults.followed.length > 0 || filteredResults.notFollowed.length > 0) && (
        <>
          {/* Followed hashtags section */}
          {filteredResults.followed.length > 0 && (
            <>
              <h5 className="mt-4 mb-3">Hashtags You Follow</h5>
              <HashtagTable
                hashtags={filteredResults.followed}
                hashtagUsageCeiling={hashtagUsageCeiling}
                onFollow={onFollow}
                onMute={onMute}
                onBlock={onBlock}
                onRemoveForceFollow={onRemoveForceFollow}
                emptyMessage="No followed hashtags match your search."
              />
            </>
          )}

          {/* Not followed hashtags section */}
          {filteredResults.notFollowed.length > 0 && (
            <>
              <h5 className="mt-4 mb-3">Other Matching Hashtags</h5>
              <HashtagTable
                hashtags={filteredResults.notFollowed}
                hashtagUsageCeiling={hashtagUsageCeiling}
                onFollow={onFollow}
                onMute={onMute}
                onBlock={onBlock}
                emptyMessage="No other hashtags match your search."
              />
            </>
          )}
        </>
      )}
    </div>
  );
}

export default SearchHashtagsView;
